import React from "react";

import { faLock } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { usePublicAccessKey } from "~/meetingRecorder/utils";
import { useModals } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";

import { GoogleAuthButton, OfficeAuthButton } from "./auth/AuthButtons";
import askFellowGraphic from "./images/ask-fellow-signup-graphic.png";
import askFellowGraphic2x from "./images/ask-fellow-signup-graphic@2x.png";

import styles from "./css/PublicRecapSignupModal.module.css";

export type PublicRecapSignupTrigger = "ask-fellow-text-input" | "ask-fellow-suggested-item" | "locked-video-playback";

export type ModalContent = {
	id: string;
	title: string | React.ReactNode;
	subtitle: string;
	image: string | null;
	image2x: string | null;
};

function PublicRecapSignupModal(props: {
	onClose: () => void;
	trigger: PublicRecapSignupTrigger;
	accessKey?: string | undefined;
}) {
	const { onClose, trigger, accessKey } = props;

	const rootURL = window.ROOT_URL ?? "https://fellow.app";
	const loginUrl = `${rootURL}/auth/login/`;

	const content: ModalContent = React.useMemo(() => {
		switch (trigger) {
			case "ask-fellow-text-input":
			case "ask-fellow-suggested-item":
				return {
					id: "ask-fellow",
					title: "Uncover Meeting Insights",
					subtitle: "Ask Fellow is like ChatGPT for your meetings! ",
					image: askFellowGraphic,
					image2x: askFellowGraphic2x,
				};
			case "locked-video-playback":
				return {
					id: "locked-video-playback",
					title: (
						<div className={styles.lockTitle}>
							<FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
							Meeting Recordings Protected
						</div>
					),
					subtitle: "You must sign up to view the meeting recording.",
					image: null,
					image2x: null,
				};
		}
	}, [trigger]);

	if (trigger) {
		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[Public Recap] Signup Modal Opened", { trigger, modal_id: content.id });
	}

	const onLoginClick = React.useCallback(() => {
		if (trigger) {
			// eslint-disable-next-line camelcase
			dispatchAnalyticsTrack("[Public Recap] Signup Modal Login Link Clicked", { modal_id: content.id, trigger });
		}
	}, [trigger, content.id]);

	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose} noBottomBorder>
				<Modal.HeaderActions />
			</Modal.Header>
			<Modal.Body noPadding className={styles.modalBody}>
				<div className={styles.content}>
					<div className={styles.title}>{content.title}</div>
					<div className={styles.subtitle}>{content.subtitle}</div>
					{content.image && (
						<img
							src={content.image}
							srcSet={`${content.image2x} 2x`}
							className={styles.integrationsGraphic}
							alt="Fellow AI Notetaker"
						/>
					)}
					<div className={cx(styles.authButtonsContainer, { [styles.extraMargin]: !content.image })}>
						<GoogleAuthButton accessKey={accessKey} trigger={trigger} contentId={content.id} />
						<OfficeAuthButton accessKey={accessKey} trigger={trigger} contentId={content.id} />
					</div>
					<div className={styles.loginText}>
						Already have a Fellow account?{" "}
						<a href={loginUrl} target="_blank" rel="noopener noreferrer" onClick={onLoginClick}>
							Log in
						</a>
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);
}

export const usePublicRecapSignupModal = () => {
	const modals = useModals();

	const publicContext = usePublicAccessKey();

	const accessKey = publicContext?.type === "recording" ? publicContext.accessKey : undefined;

	return React.useCallback(
		(trigger: PublicRecapSignupTrigger) => {
			modals.open(({ close }) => <PublicRecapSignupModal onClose={close} trigger={trigger} accessKey={accessKey} />);
		},
		[modals, accessKey],
	);
};
